import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, KeyRound, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { useBookingGate } from '@/context/BookingGateContext';
import { resolveApiBaseUrl } from '@/lib/utils';

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL);

type Step = 'email' | 'code' | 'success';

export const BookingGateModal: React.FC = () => {
  const { isOpen, close, pendingUrl } = useBookingGate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('email');
      setEmail('');
      setCode('');
      setError('');
      setLoading(false);
      setTimeout(() => emailRef.current?.focus(), 80);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 'code') {
      setTimeout(() => codeRef.current?.focus(), 80);
    }
    if (step === 'success') {
      const timer = setTimeout(() => {
        window.open(pendingUrl, '_blank', 'noopener,noreferrer');
        close();
      }, 1400);
      return () => clearTimeout(timer);
    }
  }, [step, pendingUrl, close]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [close]);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/verify/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.error || 'Failed to send code');
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/verify/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.error || 'Incorrect code');
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setCode('');
      codeRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={close}
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="relative w-full max-w-md bg-bg-secondary border border-border-subtle rounded-3xl shadow-2xl pointer-events-auto overflow-hidden">
              {/* Close */}
              <button
                onClick={close}
                className="absolute top-4 right-4 p-2 text-text-tertiary hover:text-text-primary transition-colors rounded-full hover:bg-bg-tertiary"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-7 pt-6">
                <AnimatePresence mode="wait">
                  {step === 'email' && (
                    <motion.div
                      key="email-step"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.18 }}
                    >
                      <div className="w-11 h-11 rounded-xl bg-apple-blue/10 flex items-center justify-center mb-4">
                        <Mail className="w-5 h-5 text-apple-blue" />
                      </div>
                      <h2 className="text-h3 font-semibold text-text-primary mb-1">Verify your email</h2>
                      <p className="text-body-s text-text-secondary mb-6">
                        Enter your email and we'll send a 6-digit code to confirm before opening the booking calendar.
                      </p>
                      <form onSubmit={requestCode} className="space-y-4">
                        <input
                          ref={emailRef}
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body-s text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all"
                        />
                        {error && <p className="text-body-s text-red-400">{error}</p>}
                        <button
                          type="submit"
                          disabled={loading || !email.trim()}
                          className="w-full inline-flex items-center justify-center gap-2 bg-apple-blue text-white font-medium rounded-full px-6 py-3 text-body-s hover:bg-apple-blue-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Send code <ArrowRight className="w-4 h-4" /></>
                          )}
                        </button>
                      </form>
                    </motion.div>
                  )}

                  {step === 'code' && (
                    <motion.div
                      key="code-step"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.18 }}
                    >
                      <div className="w-11 h-11 rounded-xl bg-apple-blue/10 flex items-center justify-center mb-4">
                        <KeyRound className="w-5 h-5 text-apple-blue" />
                      </div>
                      <h2 className="text-h3 font-semibold text-text-primary mb-1">Enter the code</h2>
                      <p className="text-body-s text-text-secondary mb-6">
                        We sent a 6-digit code to <span className="text-text-primary font-medium">{email}</span>.
                        It expires in 10 minutes.
                      </p>
                      <form onSubmit={confirmCode} className="space-y-4">
                        <input
                          ref={codeRef}
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          required
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000"
                          className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all tracking-[0.3em] text-center font-mono"
                        />
                        {error && <p className="text-body-s text-red-400">{error}</p>}
                        <button
                          type="submit"
                          disabled={loading || code.length !== 6}
                          className="w-full inline-flex items-center justify-center gap-2 bg-apple-blue text-white font-medium rounded-full px-6 py-3 text-body-s hover:bg-apple-blue-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Verify &amp; open calendar <ArrowRight className="w-4 h-4" /></>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setStep('email'); setCode(''); setError(''); }}
                          className="w-full text-body-s text-text-tertiary hover:text-text-secondary transition-colors"
                        >
                          Use a different email
                        </button>
                      </form>
                    </motion.div>
                  )}

                  {step === 'success' && (
                    <motion.div
                      key="success-step"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className="text-center py-6"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
                        className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4"
                      >
                        <CheckCircle className="w-7 h-7 text-green-500" />
                      </motion.div>
                      <h2 className="text-h3 font-semibold text-text-primary mb-2">Verified</h2>
                      <p className="text-body-s text-text-secondary">Opening the booking calendar…</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BookingGateModal;
